import { useState, type FormEvent } from 'react';
import { Feld, Hinweis, Knopf } from '@/components/ui';
import { useAuth } from './AuthKontext';

/**
 * Anmeldung (PHASENPLAN 0.7).
 *
 * Kein Self-Signup, keine Passwort-vergessen-Selbstbedienung — Zugänge legt
 * ausschließlich ein Admin an (SPEC Kap. 2). Der Bildschirm deckt außerdem
 * die beiden Zwischenschritte ab, die Cognito verlangen kann: erstes Passwort
 * setzen und MFA-Code bestätigen.
 */
export const AnmeldeSeite = () => {
  const { schritt, fehler, anmelden, passwortSetzen, mfaBestaetigen } = useAuth();
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [neuesPasswort, setNeuesPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [code, setCode] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [eigenerFehler, setEigenerFehler] = useState<string | null>(null);

  const absenden = async (ereignis: FormEvent) => {
    ereignis.preventDefault();
    setEigenerFehler(null);
    setLaedt(true);
    try {
      if (schritt === 'neuesPasswort') {
        if (neuesPasswort.length < 12) {
          setEigenerFehler('Das neue Passwort muss mindestens 12 Zeichen lang sein.');
          return;
        }
        if (neuesPasswort !== wiederholung) {
          setEigenerFehler('Die beiden Passwörter stimmen nicht überein.');
          return;
        }
        await passwortSetzen(neuesPasswort);
        return;
      }
      if (schritt === 'mfaCode') {
        await mfaBestaetigen(code);
        return;
      }
      await anmelden(email, passwort);
    } finally {
      setLaedt(false);
    }
  };

  const anzeigeFehler = eigenerFehler ?? fehler;

  return (
    <main className="flex min-h-full items-center justify-center bg-slate-100 px-4 py-10 dunkel:bg-slate-950">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-marke-600 text-2xl text-white">
            ✓
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 dunkel:text-slate-50">
            Übergabeprotokoll
          </h1>
          <p className="mt-1 text-sm text-slate-600 dunkel:text-slate-400">
            Bitte melden Sie sich mit Ihrem Firmenzugang an.
          </p>
        </div>

        <form
          onSubmit={absenden}
          className="space-y-4 rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200 dunkel:bg-slate-900 dunkel:ring-slate-700"
          noValidate
        >
          {anzeigeFehler && <Hinweis art="fehler">{anzeigeFehler}</Hinweis>}

          {schritt === 'neuesPasswort' && (
            <>
              <Hinweis art="info" titel="Neues Passwort vergeben">
                Ihr Zugang wurde neu eingerichtet. Bitte vergeben Sie jetzt ein eigenes Passwort.
              </Hinweis>
              <Feld
                label="Neues Passwort"
                type="password"
                autoComplete="new-password"
                value={neuesPasswort}
                onChange={(e) => setNeuesPasswort(e.target.value)}
                pflicht
                minLength={12}
                hilfetext="Mindestens 12 Zeichen, mit Groß- und Kleinbuchstaben sowie einer Ziffer."
              />
              <Feld
                label="Passwort wiederholen"
                type="password"
                autoComplete="new-password"
                value={wiederholung}
                onChange={(e) => setWiederholung(e.target.value)}
                pflicht
              />
            </>
          )}

          {schritt === 'mfaCode' && (
            <>
              <Hinweis art="info" titel="Bestätigungscode">
                Bitte geben Sie den sechsstelligen Code aus Ihrer Authenticator-App ein.
              </Hinweis>
              <Feld
                label="Code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                pflicht
                autoFocus
              />
            </>
          )}

          {(schritt === 'abgemeldet' || schritt === 'laedt') && (
            <>
              <Feld
                label="E-Mail-Adresse"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                pflicht
                autoFocus
              />
              <Feld
                label="Passwort"
                type="password"
                autoComplete="current-password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                pflicht
              />
            </>
          )}

          <Knopf type="submit" groesse="gross" volleBreite laedt={laedt}>
            {schritt === 'neuesPasswort'
              ? 'Passwort speichern'
              : schritt === 'mfaCode'
                ? 'Code bestätigen'
                : 'Anmelden'}
          </Knopf>

          <p className="pt-2 text-center text-xs leading-relaxed text-slate-500 dunkel:text-slate-400">
            Zugänge werden ausschließlich von der Verwaltung angelegt. Bei Problemen mit der
            Anmeldung wenden Sie sich bitte an Ihre Administratorin oder Ihren Administrator.
          </p>
        </form>
      </div>
    </main>
  );
};
